import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import 'moment-duration-format';

type CountdownState = {
    years: number;
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    countDown: string;
};

const initialTime = {
    years: 0,
    months: 0,
    weeks: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
};

const useCountdown = (
    endTime: EpochTimeStamp,
    formatOutput?: (
        output: Omit<CountdownState, 'countDown'>,
        timeLeft: moment.Duration
    ) => string
) => {
    const initialState: CountdownState = useMemo(
        () => ({
            ...initialTime,
            countDown: formatOutput
                ? formatOutput(initialTime, moment.duration())
                : `${initialTime.days}d ${initialTime.hours}h ${initialTime.minutes}min`,
        }),
        [formatOutput]
    );

    const [remainingTime, setRemainingTime] = useState(initialState);

    const isMounted = useRef(true);

    const getTimeRemaining = useCallback(() => {
        const currentTime = moment().unix();

        const timeLeft = moment.duration((endTime - currentTime) * 1000, 'milliseconds');
        const yearsLeft = timeLeft.years();
        const monthsLeft = timeLeft.months();
        const weeksLeft = timeLeft.weeks();
        const daysLeft = timeLeft.days();
        const hoursLeft = timeLeft.hours();
        const minsLeft = timeLeft.minutes();
        const secsLeft = timeLeft.seconds();

        const outputFormat: Omit<CountdownState, 'countDown'> = {
            years: yearsLeft,
            months: monthsLeft,
            weeks: weeksLeft,
            days: daysLeft,
            hours: hoursLeft,
            minutes: minsLeft,
            seconds: secsLeft,
        };

        setRemainingTime({
            ...outputFormat,
            countDown: formatOutput
                ? formatOutput(outputFormat, timeLeft)
                : `${daysLeft}d ${hoursLeft}h ${minsLeft}min`,
        });
    }, [endTime, formatOutput]);

    useEffect(() => {
        if (!endTime) return;

        if (moment() > moment.unix(endTime)) return;

        if (remainingTime.countDown === initialState.countDown) {
            getTimeRemaining();
        }

        const interval = setInterval(() => {
            getTimeRemaining();
        }, 1000);

        return () => {
            clearInterval(interval);
            isMounted.current = false;
        };
    }, [endTime, initialState, remainingTime, getTimeRemaining]);

    return remainingTime;
};

export default useCountdown;
